import React, { useEffect, useState } from "react";
import { addDays, isValid } from "date-fns";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { Button } from "components/common/Button";
import { CopyButton } from "components/common/CopyButton";
import { DatePickerInput } from "components/common/DatePickerInput";
import { SegmentedTabs } from "components/common/SegmentedTabs";
import { TextInput } from "components/common/TextInput";
import { Dialog } from "@/components/main/utils/Dialog";
import { DialogField } from "@/components/main/utils/DialogField";
import { useTokensStore } from "components/main/stores/tokensStore";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { OrgBadge } from "./OrgBadge";
import { formatDay, minutesUntil, normalizeName } from "./utils";

const EXPIRY_OPTIONS = [
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "1 year" },
  { value: "custom", label: "Custom" },
] as const;
type Expiry = (typeof EXPIRY_OPTIONS)[number]["value"];
const SCOPE_OPTIONS = [
  { value: "read", label: "Read-only" },
  { value: "write", label: "Read & write" },
] as const;

interface TokenDialogProps {
  onGenerate: () => Promise<void>;
  onDone: () => void;
}

export const TokenDialog: React.FC<TokenDialogProps> = ({
  onGenerate,
  onDone,
}) => {
  const {
    dialogOpen,
    newToken,
    generatedToken,
    isGeneratingToken,
    tokens,
    updateNewToken,
    closeDialog,
    resetDialog,
  } = useTokensStore();
  const [expiry, setExpiry] = useState<Expiry>("30");
  const [customDate, setCustomDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (!dialogOpen) {
      setExpiry("30");
      setCustomDate(null);
      setDateError(null);
      resetDialog();
    }
  }, [dialogOpen, resetDialog]);

  const name = newToken.description;
  const duplicate =
    normalizeName(name) !== "" &&
    tokens.some(
      (t) =>
        t.get_status === "active" &&
        normalizeName(t.get_description) === normalizeName(name),
    );
  const today = new Date();
  const maxDate = addDays(today, 365);
  const customOk =
    expiry !== "custom" ||
    (customDate !== null && isValid(customDate) && !dateError);
  const canGenerate = name.trim() !== "" && !duplicate && customOk;

  const pickExpiry = (value: Expiry) => {
    setExpiry(value);
    // Custom starts where the preset would have ended.
    const end = addDays(
      today,
      Number(value === "custom" ? expiry : value) || 30,
    );
    if (value === "custom") {
      setCustomDate(end);
      setDateError(null);
    }
    updateNewToken({ expiration_time: minutesUntil(end) });
  };
  const pickDate = (date: Date | null) => {
    setCustomDate(date);
    if (date && isValid(date))
      updateNewToken({ expiration_time: minutesUntil(date) });
  };
  const onDateError = (reason: string | null) =>
    setDateError(
      !reason
        ? null
        : reason === "invalidDate"
          ? "Use DD/MM/YYYY"
          : `Pick a date up to ${formatDay(maxDate)}`,
    );

  if (generatedToken) {
    return (
      <Dialog
        open={dialogOpen}
        title="Copy your token"
        onClose={onDone}
        showCloseButton={false}
        actions={
          <Button variant="primary" autoFocus onClick={onDone}>
            Done
          </Button>
        }
      >
        <div className="flex items-center gap-2 rounded-app bg-warning-bg px-3 py-2 text-small">
          <ErrorOutlineOutlinedIcon className="icon-sm text-warning" />
          You won't see it again.
        </div>
        <div className="flex items-start gap-1 rounded-app border border-border bg-bg py-1 pl-2 pr-1">
          <span className="flex-1 min-w-0 py-1 font-mono text-small break-all select-text">
            {generatedToken}
          </span>
          <CopyButton
            text={generatedToken}
            ariaLabel="Copy token"
            onCopy={() => showNotificationGlobal("Copied", "success")}
          />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={dialogOpen}
      title="New token"
      onClose={closeDialog}
      actions={
        <>
          <Button variant="secondary" onClick={closeDialog}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canGenerate || isGeneratingToken}
            loading={isGeneratingToken}
            onClick={() => void onGenerate()}
          >
            Generate
          </Button>
        </>
      }
    >
      <DialogField
        label={
          <>
            Description
            <span className="text-error ml-0.5" aria-hidden="true">
              *
            </span>
          </>
        }
      >
        <TextInput
          value={name}
          onChange={(value) => updateNewToken({ description: value })}
          placeholder="Add a description"
          maxLength={60}
          autoFocus
          ariaInvalid={duplicate}
          disabled={isGeneratingToken}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canGenerate) void onGenerate();
          }}
          className="input-custom-sm w-full"
        />
        {duplicate && (
          <span role="alert" className="block mt-1 text-small text-error">
            Description already in use
          </span>
        )}
      </DialogField>
      <DialogField label="Organization">
        <OrgBadge />
      </DialogField>
      <DialogField label="Scope">
        <SegmentedTabs
          options={SCOPE_OPTIONS}
          value={newToken.scope}
          onChange={(scope) => updateNewToken({ scope })}
        />
      </DialogField>
      <DialogField label="Expiration">
        <SegmentedTabs
          options={EXPIRY_OPTIONS}
          value={expiry}
          onChange={pickExpiry}
          wrap
        />
        {expiry === "custom" ? (
          <div className="flex flex-col gap-1 mt-1">
            <div className="w-50 max-w-full">
              <DatePickerInput
                value={customDate}
                onChange={pickDate}
                minDate={addDays(today, 1)}
                maxDate={maxDate}
                format="dd/MM/yyyy"
                onError={onDateError}
                disabled={isGeneratingToken}
              />
            </div>
            {dateError && (
              <span role="alert" className="text-small text-error">
                {dateError}
              </span>
            )}
          </div>
        ) : (
          <span className="block mt-1 text-fg-muted-regular-small tabular-nums">
            Ends{" "}
            <b className="text-fg font-medium">
              {formatDay(addDays(today, Number(expiry)))}
            </b>
          </span>
        )}
      </DialogField>
    </Dialog>
  );
};
